import { useState, useEffect } from "react";
import { Package, Plus, RefreshCw, BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";

export default function ERPSCM() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("materials");
  const [materials, setMaterials] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [mrpResult, setMrpResult] = useState(null);
  const [showNewMaterial, setShowNewMaterial] = useState(false);
  const [showNewPO, setShowNewPO] = useState(false);

  const [newMaterial, setNewMaterial] = useState({
    material_code: "", description: "", material_type: "FINISHED", base_uom: "EA",
    standard_cost: 0, selling_price: 0, reorder_point: 0, safety_stock: 0, initial_stock: 0
  });
  const [newPO, setNewPO] = useState({
    vendor_id: "", items: [{ material_id: "", quantity_ordered: 1, unit_price: 0 }]
  });

  const fetchMaterials = async () => {
    try {
      const res = await axios.get("/api/erp/materials");
      setMaterials(res.data.materials || []);
    } catch (err) { toast({ title: "Failed to load materials", variant: "destructive" }); }
  };

  const fetchInventory = async () => {
    try {
      const res = await axios.get("/api/erp/inventory");
      setInventory(res.data.inventory || []);
    } catch (err) { /* ignore */ }
  };

  const fetchPOs = async () => {
    try {
      const res = await axios.get("/api/erp/purchase-orders");
      setPurchaseOrders(res.data.purchase_orders || []);
    } catch (err) { /* ignore */ }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get("/api/erp/vendors");
      setVendors(res.data.vendors || []);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    fetchMaterials();
    fetchInventory();
    fetchVendors();
  }, []);

  const handleCreateMaterial = async () => {
    try {
      await axios.post("/api/erp/materials", newMaterial);
      toast({ title: "Material created" });
      setShowNewMaterial(false);
      fetchMaterials();
      fetchInventory();
    } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreatePO = async () => {
    try {
      await axios.post("/api/erp/purchase-orders", newPO);
      toast({ title: "Purchase order created" });
      setShowNewPO(false);
      fetchPOs();
    } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleRunMRP = async () => {
    try {
      const res = await axios.post("/api/erp/mrp", { name: "MRP " + new Date().toLocaleDateString(), planning_horizon_days: 90 });
      setMrpResult(res.data);
      toast({ title: "MRP completed: " + (res.data.recommendations?.length || 0) + " recommendations" });
    } catch (err) { toast({ title: "MRP failed", variant: "destructive" }); }
  };

  const renderMaterials = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Materials Master</h2>
        <Dialog open={showNewMaterial} onOpenChange={setShowNewMaterial}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Material</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Material</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div><Label>Material Code</Label><Input value={newMaterial.material_code} onChange={e => setNewMaterial(p => ({ ...p, material_code: e.target.value }))} /></div>
              <div><Label>Description</Label><Input value={newMaterial.description} onChange={e => setNewMaterial(p => ({ ...p, description: e.target.value }))} /></div>
              <div><Label>Type</Label>
                <Select value={newMaterial.material_type} onValueChange={v => setNewMaterial(p => ({ ...p, material_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RAW">Raw Material</SelectItem>
                    <SelectItem value="SEMI-FINISHED">Semi-Finished</SelectItem>
                    <SelectItem value="FINISHED">Finished</SelectItem>
                    <SelectItem value="SERVICE">Service</SelectItem>
                    <SelectItem value="TRADING">Trading</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Unit of Measure</Label>
                  <Select value={newMaterial.base_uom} onValueChange={v => setNewMaterial(p => ({ ...p, base_uom: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="EA">Each</SelectItem><SelectItem value="KG">Kilogram</SelectItem><SelectItem value="L">Liter</SelectItem><SelectItem value="M">Meter</SelectItem><SelectItem value="BOX">Box</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Initial Stock</Label><Input type="number" value={newMaterial.initial_stock} onChange={e => setNewMaterial(p => ({ ...p, initial_stock: parseInt(e.target.value) || 0 }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Standard Cost</Label><Input type="number" value={newMaterial.standard_cost} onChange={e => setNewMaterial(p => ({ ...p, standard_cost: parseFloat(e.target.value) || 0 }))} /></div>
                <div><Label>Selling Price</Label><Input type="number" value={newMaterial.selling_price} onChange={e => setNewMaterial(p => ({ ...p, selling_price: parseFloat(e.target.value) || 0 }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Reorder Point</Label><Input type="number" value={newMaterial.reorder_point} onChange={e => setNewMaterial(p => ({ ...p, reorder_point: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label>Safety Stock</Label><Input type="number" value={newMaterial.safety_stock} onChange={e => setNewMaterial(p => ({ ...p, safety_stock: parseInt(e.target.value) || 0 }))} /></div>
              </div>
              <Button onClick={handleCreateMaterial} className="w-full">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Code</TableHead><TableHead>Description</TableHead><TableHead>Type</TableHead><TableHead>UOM</TableHead><TableHead>Cost</TableHead><TableHead>Price</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {materials.map(m => (
              <TableRow key={m.id}>
                <TableCell className="font-mono text-xs">{m.material_code}</TableCell>
                <TableCell>{m.description}</TableCell>
                <TableCell><Badge variant="outline">{m.material_type}</Badge></TableCell>
                <TableCell>{m.base_uom}</TableCell>
                <TableCell>${m.standard_cost?.toFixed(2)}</TableCell>
                <TableCell>${m.selling_price?.toFixed(2)}</TableCell>
              </TableRow>
            ))}
            {materials.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No materials</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderInventory = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Inventory Stock</h2>
        <Button variant="outline" size="sm" onClick={fetchInventory}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Material</TableHead><TableHead>Warehouse</TableHead><TableHead>On Hand</TableHead><TableHead>Reserved</TableHead><TableHead>Available</TableHead><TableHead>Status</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {inventory.map((inv, i) => {
              const onHand = inv.quantity_on_hand || 0;
              const reserved = inv.quantity_reserved || 0;
              const available = onHand - reserved;
              const reorder = inv.material?.reorder_point || 0;
              return (
                <TableRow key={i}>
                  <TableCell>{inv.material?.description || inv.material_id}</TableCell>
                  <TableCell>{inv.warehouse_id || "default"}</TableCell>
                  <TableCell>{onHand}</TableCell>
                  <TableCell>{reserved}</TableCell>
                  <TableCell className={available < 0 ? "text-red-600 font-bold" : available < reorder ? "text-orange-600" : ""}>{available}</TableCell>
                  <TableCell><Badge variant={available < 0 ? "destructive" : available < reorder ? "warning" : "secondary"}>{available < 0 ? "Stockout" : available < reorder ? "Low" : "OK"}</Badge></TableCell>
                </TableRow>
              );
            })}
            {inventory.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No inventory data</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderPurchaseOrders = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Purchase Orders</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPOs}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Dialog open={showNewPO} onOpenChange={setShowNewPO}>
            <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New PO</Button></DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                <div><Label>Vendor</Label>
                  <Select value={newPO.vendor_id} onValueChange={v => setNewPO(p => ({ ...p, vendor_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm">Items</span>
                  <Button variant="outline" size="sm" onClick={() => setNewPO(p => ({ ...p, items: [...p.items, { material_id: "", quantity_ordered: 1, unit_price: 0 }] }))}>Add Item</Button>
                </div>
                {newPO.items.map((item, i) => (
                  <div key={i} className="flex gap-2">
                    <div className="flex-1">
                      <Select value={item.material_id} onValueChange={v => { const items = [...newPO.items]; items[i].material_id = v; setNewPO(p => ({ ...p, items })); }}>
                        <SelectTrigger><SelectValue placeholder="Material" /></SelectTrigger>
                        <SelectContent>{materials.map(m => <SelectItem key={m.id} value={m.id}>{m.material_code} - {m.description}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-20"><Input type="number" placeholder="Qty" value={item.quantity_ordered} onChange={e => { const items = [...newPO.items]; items[i].quantity_ordered = parseInt(e.target.value) || 0; setNewPO(p => ({ ...p, items })); }} /></div>
                    <div className="w-24"><Input type="number" placeholder="Price" value={item.unit_price} onChange={e => { const items = [...newPO.items]; items[i].unit_price = parseFloat(e.target.value) || 0; setNewPO(p => ({ ...p, items })); }} /></div>
                  </div>
                ))}
                <Button onClick={handleCreatePO} className="w-full">Create PO</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>PO #</TableHead><TableHead>Vendor</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead>AI Score</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {purchaseOrders.map(po => (
              <TableRow key={po.id}>
                <TableCell className="font-mono text-xs">{po.po_number}</TableCell>
                <TableCell>{po.vendor_id?.slice(0, 12)}...</TableCell>
                <TableCell>${po.total_amount?.toFixed(2)}</TableCell>
                <TableCell><Badge variant={po.status === "RECEIVED" ? "default" : po.status === "IN_TRANSIT" ? "warning" : "secondary"}>{po.status}</Badge></TableCell>
                <TableCell>{po.order_date}</TableCell>
                <TableCell>{po.ai_suggested_vendor_score ? po.ai_suggested_vendor_score + "/100" : "-"}</TableCell>
              </TableRow>
            ))}
            {purchaseOrders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No purchase orders</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderMRP = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Material Requirements Planning</h2>
        <Button onClick={handleRunMRP}><BarChart3 className="h-4 w-4 mr-1" /> Run MRP</Button>
      </div>
      {mrpResult ? (
        <div className="space-y-4">
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="p-4">
              <p className="text-sm text-indigo-800">MRP Run: {mrpResult.name} | Status: {mrpResult.status} | Recommendations: {mrpResult.recommendations?.length || 0}</p>
            </CardContent>
          </Card>
          {mrpResult.recommendations?.length > 0 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Material</TableHead><TableHead>Type</TableHead><TableHead>Quantity</TableHead><TableHead>Required By</TableHead><TableHead>Priority</TableHead><TableHead>AI Confidence</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {mrpResult.recommendations.map((rec, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{rec.material_code}</TableCell>
                      <TableCell><Badge variant="outline">{rec.recommendation_type}</Badge></TableCell>
                      <TableCell>{rec.quantity}</TableCell>
                      <TableCell>{rec.required_by_date}</TableCell>
                      <TableCell><Badge variant={rec.priority === 1 ? "destructive" : "warning"}>{rec.priority === 1 ? "High" : "Medium"}</Badge></TableCell>
                      <TableCell>{rec.ai_confidence ? rec.ai_confidence.toFixed(0) + "%" : "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ) : (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Click "Run MRP" to generate material requirements</CardContent></Card>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Supply Chain Management</h1>
        <p className="text-muted-foreground">Materials, inventory, procurement & MRP</p>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="materials">Materials</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="purchase-orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="mrp">MRP</TabsTrigger>
        </TabsList>
        <TabsContent value="materials" className="mt-4">{renderMaterials()}</TabsContent>
        <TabsContent value="inventory" className="mt-4">{renderInventory()}</TabsContent>
        <TabsContent value="purchase-orders" className="mt-4">{renderPurchaseOrders()}</TabsContent>
        <TabsContent value="mrp" className="mt-4">{renderMRP()}</TabsContent>
      </Tabs>
    </div>
  );
}
